import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { PlayerLobbyRow } from "./PlayerLobbyRow";
import moment, { Duration, Moment } from "moment";
import useInterval from "use-interval";
import { useUpdateGameMutation } from "../../hooks/useGameUpdateMutation";
import { MAX_PLAYERS, GAME_URL, GAME_START_DELAY } from "../../config";
import { motion } from "framer-motion";
import CountdownBG from "../../assets/images/countdown-bg.svg";
import CountdownH from "../../assets/images/countdown-h.svg";
import classNames from "classnames";
import "./game-lobby.scss";
import { PlayerContext } from "../CurrentPlayerContext";
import GameInfo from "../../lib/models/GameInfo";
import { UserContext } from "../UserContext";
import useCopy from "@react-hook/copy";
declare const window: any;

const getTimeRemaining = (start: Moment) => {
  return moment.duration(start.diff(moment()));
};

const GameLobby = ({ game }: { game: GameInfo }) => {
  const history = useHistory();
  const { changeName } = useContext(UserContext);
  const { user } = useContext(UserContext);
  const [gameStarted, setGameStarted] = useState(false);

  if (!game) throw new Error("No game loaded"); // not sure how to better do this right now
  const [player, { updateName }] = useContext(PlayerContext);

  const { id } = game;
  const [updateGame] = useUpdateGameMutation(id);

  const [remaining, setRemaining] = useState<Duration>(() =>
    getTimeRemaining(game.dateStart)
  );

  const [copySuccess, setCopySuccess] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    window.bindTracking({
      data: {
        pageviewevent: "1",
        screenName: "Game Lobby",
        screenSection: "Game Lobby",
        interactionName: "Game Lobby"
      }
    });
  }, []);

  useInterval(() => setRemaining(getTimeRemaining(game.dateStart)), 1000);

  const startNow = () => {
    const newStart = moment().add(3, "s");
    updateGame(0, newStart);
  };

  useEffect(() => {
    if (remaining.asSeconds() <= 0 && !isStarting) {
      startNow();
      setIsStarting(true);
      triggerGameStartEvent();
      history.push(`/play/${id}`); //version that uses modern subscriptions
    }
  }, [history, id, remaining]);

  const updatePlayerCharacter = (char: any) => {
    const charName = char.userNameBase + Math.floor(Math.random() * 100) + 1; //add random number so username unique

    updateName(charName);
  };

  useEffect(() => {
    changeName(player.name);
  }, [player.name]);

  const buildPlayerRow = (
    key: string,
    name: string,
    isOpen: boolean,
    isCurrentPlayer: boolean
  ) => {
    return (
      <PlayerLobbyRow
        key={key}
        name={name}
        isOpen={isOpen}
        onUpdatePlayer={updatePlayerCharacter}
        isCurrentPlayer={isCurrentPlayer}
      />
    );
  };

  const emptySlots = [];
  for (let i = game.getTotalPlayers(); i < MAX_PLAYERS; i++) {
    emptySlots.push(buildPlayerRow(`empty-${i}`, "Empty", true, false));
  }

  const triggerEvent = (interactionName: string) => {
    window.bindTracking({
      type: "interaction-event",
      data: {
        interactionEvent: 1,
        screenName: "Game Lobby",
        screenSection: "Game Lobby",
        interactionName
      }
    });
  };

  const triggerGameStartEvent = () => {
    if (!gameStarted) {
      setGameStarted(true);
      window.bindTracking({
        type: "interaction-event",
        data: {
          interactionEvent: 1,
          screenName: "Game Start",
          screenSection: "Game",
          interactionName: "Game Start",
          gameType: players.length === 1 ? "private" : "public"
        }
      });
    }
  };

  const delayGame = () => {
    triggerEvent("Delay Game Start");
    const mins = moment.utc(remaining.asMilliseconds()).format("m");
    const secs = moment.utc(remaining.asMilliseconds()).format("s");
    const newStart = moment().add(mins, "m").add(secs, "s").add(30, "s"); //30000
    const duration = moment.duration(moment().diff(newStart));
    if (duration.asMinutes() > -10) {
      updateGame(0, newStart);
    }
  };

  const { players } = game;

  const { copied, copy, reset } = useCopy(
    `${process.env.REACT_APP_GAME_URL || GAME_URL}/#/join/${id}`
  );

  function handleInviteClick() {
    triggerEvent("Invite Players");
    copy();
    setCopySuccess(true);
  }

  const mins = isStarting
    ? 0
    : moment.utc(remaining.asMilliseconds()).format("mm").substr(1, 2);
  const secsOne = isStarting
    ? 0
    : moment.utc(remaining.asMilliseconds()).format("ss").substr(0, 1);
  const secsTwo = isStarting
    ? 0
    : moment.utc(remaining.asMilliseconds()).format("ss").substr(1, 2);

  return (
    <div className="inner-container">
      <div className="game-lobby">
        <motion.div
          className="game-lobby-inner"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 0, transition: { type: "spring" } }}
        >
          <div className="countdown">
            <div
              className={classNames({
                "countdown-title": true,
                host: players[0].id === user.id
              })}
            >
              {players[0].id === user.id
                ? "Game starting in..."
                : "Waiting for host to start game..."}
            </div>
            <div className="countdown-timer">
              <div className="countdown-timer-h">
                <p>{mins}</p>
                <img src={CountdownH} />
              </div>

              <div className="countdown-timer-ss">
                <p>{secsOne}</p>
                <img src={CountdownBG} />
              </div>
              <div className="countdown-timer-ss">
                <p>{secsTwo}</p>
                <img src={CountdownBG} />
              </div>
            </div>
          </div>
          <div className="lobby">
            <div className="lobby-title">
              Current Players - {game.getTotalPlayers()}/4
            </div>
            <div className="lobby-group">
              {/* Player Row */}
              {players?.map((p, i) =>
                buildPlayerRow(
                  `${i}-${p.id}`,
                  p.name,
                  false,
                  p.id === player.id
                )
              )}
              {emptySlots}
            </div>
          </div>
          <div
            className={classNames({
              "button-group": true,
              host: players[0].id === user.id
            })}
          >
            {players[0].id === user.id ? (
              <button className="button button-green snow-two-green" onClick={startNow}>
                Start Game Now
              </button>
            ) : null}
            {players[0].id === user.id ? (
              <button
                className="button button-red delay-button snow-two-red"
                onClick={delayGame}
              >
                Delay Start Time
              </button>
            ) : null}

            {copySuccess ? (
              <button
                className="button button-green"
                onClick={() => handleInviteClick()}
              >
                Link Copied
              </button>
            ) : players[0].id === user.id ? (
              <button
                className="button button-blue snow-one-blue"
                onClick={() => handleInviteClick()}
              >
                Invite Players
              </button>
            ) : (
              ""
            )}
            {players[0].id !== user.id && (
              <button
                className="button button-red snow-two-red"
                onClick={() => history.push("/")}
              >
                Exit Lobby
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
export default GameLobby;
